import { useState, useContext, useEffect } from "react"
import { gql, useQuery } from "@apollo/client"
import _ from "lodash"

import { Field, useFormikContext } from "formik"

import { MultiSelect } from "primereact/multiselect"
import { FormContext } from "../../../context/formContext"
import { UserContext } from "../../../context/userContext"

import styles from "../../../styles/Form.module.css"

export default function ManageLocationIds({ field, block, index }) {
  const [activeLocations, setActiveLocations] = useState([])
  const [inactiveLocations, setInactiveLocations] = useState([])

  const [visible, setVisible] = useState("block")

  const checkIfRequired = () => {
    try {
      return JSON.parse(field.field_validation.required[0])
    } catch (error) {
      return false
    }
  }
  const isRequired = checkIfRequired()
  const formik = useFormikContext()
  const form = useContext(FormContext)
  const userCtx = useContext(UserContext)

  let isDisabled = form ? form.blocksSaved[block] : false
  isDisabled = field.read_only ? true : isDisabled

  const GET_LOCATIONS = gql`
    query getLocations(
      $staff_id: ID!
      $organization_id: ID!
      $participant_membership_id: ID!
    ) {
      locations(staff_id: $staff_id, organization_id: $organization_id) {
        key: location_id
        label: location_name
        status: location_status
      }
      locationsByMembershipId(
        staff_id: $staff_id
        organization_id: $organization_id
        participant_membership_id: $participant_membership_id
      ) {
        key: location_id
        label: location_name
      }
    }
  `
  const { loading } = useQuery(GET_LOCATIONS, {
    fetchPolicy: "network-only",
    variables: {
      staff_id: userCtx.session.user.staff_id,
      organization_id: userCtx.session.user.organization_id,
      participant_membership_id: userCtx.participant_membership_id,
    },
    onCompleted: ({ locations, locationsByMembershipId }) => {
      if (!_.isEmpty(locations)) {
        const activeLocationsList =
          locations
            .filter((location) => location.status === "ACTIVE")
            .map((location) => {
              delete location.status
              return location
            }) || []
        const inactiveLocationsList =
          locations
            .filter((location) => location.status === "INACTIVE")
            .map((location) => {
              delete location.status
              return location
            }) || []

        setActiveLocations(activeLocationsList)
        setInactiveLocations(inactiveLocationsList)

        formik.setFieldValue(field.field_name, locationsByMembershipId)
      } else {
        formik.setFieldValue(field.field_name, null)
      }
    },

    skip: !userCtx,
  })

  useEffect(() => {
    if (block === "participant__manage_services") {
      const manageServicesType = formik?.getFieldProps("manage_services_type")
      setVisible("none")
      if (
        manageServicesType.value === "Location assignment" ||
        manageServicesType.value === "Location reassignment"
      ) {
        setVisible("block")
      }
    } else {
      setVisible("block")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik])

  const handleSelectLocationIds = (value) => {
    if (value === null) {
      formik.setFieldValue(field.field_name, "")
    } else {
      const fieldCurrentValue = formik.values[field.field_name] || []
      // inActiveLocations should remain as they are, is handle with another function
      const currentInActiveLocations =
        fieldCurrentValue.filter((item) => {
          return inactiveLocations.find(
            (inactiveItem) => inactiveItem.key === item.key
          )
        }) || []
      const newValue = [...currentInActiveLocations, ...value]
      formik.setFieldValue(field.field_name, newValue)
    }
  }

  const handleDiselectInactiveLocationIds = (value) => {
    // Empty array means all locations are deselected
    if (value.length === 0) {
      const selectedLocations = formik.values[field.field_name].filter((item) =>
        activeLocations.find((activeItem) => activeItem.key === item.key)
      )
      formik.setFieldValue(field.field_name, selectedLocations)
    } else {
      const fieldCurrentValue = formik.values[field.field_name]
      // activeLocations should remain as they are
      const currentActiveLocations = fieldCurrentValue.filter((item) => {
        return activeLocations.find((activeItem) => activeItem.key === item.key)
      })
      // value is the deselected inactive locations
      const filteredLocations = [...currentActiveLocations, ...value]
      setInactiveLocations(value)
      formik.setFieldValue(field.field_name, filteredLocations)
    }
  }

  const handleMultiSelectRender = (formikField) => {
    const currentFormValues = formikField?.value || []

    const currentInactiveSelectedLocations = currentFormValues.filter((item) =>
      inactiveLocations.find((inactiveItem) => inactiveItem.key === item.key)
    )

    const renderInactiveLocations = () => {
      const displayInactiveLocationList =
        currentInactiveSelectedLocations.length > 0

      if (!displayInactiveLocationList) return null

      return (
        <div>
          <label className={styles.label}>Inactive Locations ID(s)</label>
          <div className={styles.description}>
            Inactive Locations can only be deselected.
          </div>
          <MultiSelect
            placeholder="Unselect only"
            id={field.field_name}
            name={formikField.name}
            value={currentInactiveSelectedLocations}
            onChange={(e) => handleDiselectInactiveLocationIds(e.value)}
            onBlur={(e) => formik.handleBlur(e)}
            disabled={isDisabled}
            options={currentInactiveSelectedLocations}
          />
        </div>
      )
    }

    const currentActiveSelectedLocations = currentFormValues.filter((item) =>
      activeLocations.find((activeItem) => activeItem.key === item.key)
    )

    return (
      <>
        <MultiSelect
          placeholder="Please select..."
          id={field.field_name}
          name={formikField.name}
          value={currentActiveSelectedLocations}
          onChange={(e) => handleSelectLocationIds(e.value)}
          onBlur={(e) => formik.handleBlur(e)}
          disabled={isDisabled}
          options={activeLocations}
        />

        {renderInactiveLocations()}
      </>
    )
  }

  if (loading) return null

  return (
    <div
      className={styles.field}
      style={{
        order: index,
        display: visible,
      }}
    >
      <label className={styles.label}>
        {field.field_label}
        {isRequired && <span className={styles.asterisk}> *</span>}
      </label>
      {field.field_description && (
        <div className={styles.description}>{field.field_description}</div>
      )}
      <Field name={field.field_name}>
        {({ field: formikField, meta, form }) => (
          <>
            {handleMultiSelectRender(formikField)}
            {meta.touched && meta.error && (
              <div className={styles.errFieldMsg}>{meta.error}</div>
            )}
          </>
        )}
      </Field>
    </div>
  )
}
